import { useEffect, useState } from 'react';
import {
  Plus, Save, Trash2, GraduationCap, Users as UsersIcon, User as UserIcon,
  Sparkles, ShieldCheck, Eye, EyeOff, ArrowUp, ArrowDown,
} from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, Modal, Field, Input, Textarea, Select, StatusLine,
} from '../components/ui';

type PackageType = 'group_class' | 'private_lesson' | 'clinic' | 'camp' | 'membership' | 'other';

type Package = {
  id: string;
  club_id: string;
  name: string;
  package_type: PackageType;
  description: string | null;
  schedule_text: string | null;
  duration_minutes: number | null;
  num_sessions: number;
  per_session_price: number | null;
  total_price: number;
  currency: string;
  level_label: string | null;
  audience: string | null;
  is_active: boolean;
  display_order: number;
};

const TYPE_META: Record<PackageType, { label: string; icon: any; tone: 'lime' | 'blue' | 'amber' | 'green' | 'gray' }> = {
  group_class:    { label: 'Group class',      icon: UsersIcon,      tone: 'lime' },
  private_lesson: { label: 'Private bundle',   icon: UserIcon,       tone: 'blue' },
  clinic:         { label: 'Clinic',           icon: Sparkles,       tone: 'amber' },
  camp:           { label: 'Camp',             icon: Sparkles,       tone: 'amber' },
  membership:     { label: 'Membership',       icon: ShieldCheck,    tone: 'green' },
  other:          { label: 'Package',          icon: GraduationCap,  tone: 'gray' },
};

/**
 * Club managers (owner / manager — not schedule_manager since this is
 * brand-level offering work) create and edit the packages that show
 * on the customer brand page. RLS keeps non-managers out.
 */
export default function ClubPackages({ clubId }: { clubId: string }) {
  const [rows, setRows] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Package | 'new' | null>(null);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await (supabase.from as any)('club_packages')
      .select('*')
      .eq('club_id', clubId)
      .order('display_order', { ascending: true })
      .order('total_price', { ascending: true });
    setLoading(false);
    if (error) { setMsg({ kind: 'error', text: error.message }); return; }
    setRows((data ?? []) as Package[]);
  };

  useEffect(() => { load(); }, [clubId]);

  const setActive = async (p: Package, is_active: boolean) => {
    setMsg(null);
    const { error } = await (supabase.from as any)('club_packages')
      .update({ is_active }).eq('id', p.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { load(); setMsg({ kind: 'success', text: `${p.name} ${is_active ? 'shown to customers' : 'hidden'}.` }); }
  };

  const remove = async (p: Package) => {
    if (!confirm(`Delete "${p.name}"? This can't be undone.`)) return;
    setMsg(null);
    const { error } = await (supabase.from as any)('club_packages').delete().eq('id', p.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { load(); setMsg({ kind: 'success', text: 'Deleted.' }); }
  };

  const reorder = async (p: Package, dir: -1 | 1) => {
    const idx = rows.findIndex((r) => r.id === p.id);
    const swap = rows[idx + dir];
    if (!swap) return;
    setMsg(null);
    // Swap display_order values.
    const a = (supabase.from as any)('club_packages')
      .update({ display_order: swap.display_order }).eq('id', p.id);
    const b = (supabase.from as any)('club_packages')
      .update({ display_order: p.display_order }).eq('id', swap.id);
    const [ra, rb] = await Promise.all([a, b]);
    if (ra.error || rb.error) setMsg({ kind: 'error', text: (ra.error ?? rb.error).message });
    else load();
  };

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Club"
        title="Programs & packages"
        subtitle="Group classes, private lesson bundles, clinics, and memberships customers see on your brand page."
        actions={<Button icon={Plus} onClick={() => setEditing('new')}>Add package</Button>}
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      <Section className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading packages…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={GraduationCap}
            title="No packages yet"
            description="Add your first program — group classes, private lesson bundles, summer camps, or memberships."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Order</th>
                <th className="text-left px-5 py-3 font-semibold">Package</th>
                <th className="text-left px-5 py-3 font-semibold">Schedule</th>
                <th className="text-left px-5 py-3 font-semibold">Price</th>
                <th className="text-left px-5 py-3 font-semibold">Visibility</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const M = TYPE_META[p.package_type] ?? TYPE_META.other;
                const Icon = M.icon;
                const perSession = p.per_session_price ?? (p.num_sessions > 0 ? p.total_price / p.num_sessions : null);
                return (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-5 py-3 text-xs">
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          disabled={i === 0}
                          onClick={() => reorder(p, -1)}
                          className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40 inline-flex items-center justify-center"
                          aria-label="Move up"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          disabled={i === rows.length - 1}
                          onClick={() => reorder(p, 1)}
                          className="w-6 h-6 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-40 inline-flex items-center justify-center"
                          aria-label="Move down"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5">
                          <Badge tone={M.tone} icon={Icon}>{M.label}</Badge>
                        </span>
                      </div>
                      <div className="font-medium mt-1">{p.name}</div>
                      {p.description && (
                        <div className="text-xs text-gray-500 line-clamp-1 mt-0.5 max-w-md">
                          {p.description}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {p.level_label && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700">{p.level_label}</span>}
                        {p.audience && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700">{p.audience}</span>}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-gray-700 max-w-[14rem]">
                      <div>{p.schedule_text ?? '—'}</div>
                      <div className="text-gray-400 mt-0.5">
                        {p.num_sessions > 1 ? `${p.num_sessions} sessions` : '1 session'}
                        {p.duration_minutes ? ` · ${p.duration_minutes}min` : ''}
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {p.currency} {Number(p.total_price).toFixed(0)}
                      </div>
                      {perSession != null && p.num_sessions > 1 && (
                        <div className="text-[11px] text-gray-500">
                          ≈ {p.currency} {Number(perSession).toFixed(0)} / session
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => setActive(p, !p.is_active)}
                        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                          p.is_active
                            ? 'bg-[#c6f806]/30 border-[#2d5016]/30 text-[#2d5016]'
                            : 'bg-white border-gray-200 text-gray-500'
                        }`}
                      >
                        {p.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {p.is_active ? 'Live' : 'Hidden'}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditing(p)}>Edit</Button>
                        <Button size="sm" variant="danger" icon={Trash2} onClick={() => remove(p)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      {editing && (
        <PackageForm
          clubId={clubId}
          existing={editing === 'new' ? null : editing}
          nextDisplayOrder={rows.length === 0 ? 0 : Math.max(...rows.map((r) => r.display_order)) + 1}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function PackageForm({
  clubId, existing, nextDisplayOrder, onClose, onSaved,
}: {
  clubId: string;
  existing: Package | null;
  nextDisplayOrder: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !existing;
  const [name, setName] = useState(existing?.name ?? '');
  const [packageType, setPackageType] = useState<PackageType>(existing?.package_type ?? 'group_class');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [scheduleText, setScheduleText] = useState(existing?.schedule_text ?? '');
  const [duration, setDuration] = useState(existing?.duration_minutes != null ? String(existing.duration_minutes) : '60');
  const [numSessions, setNumSessions] = useState(String(existing?.num_sessions ?? 1));
  const [totalPrice, setTotalPrice] = useState(existing?.total_price != null ? String(existing.total_price) : '');
  const [perSessionPrice, setPerSessionPrice] = useState(existing?.per_session_price != null ? String(existing.per_session_price) : '');
  const [levelLabel, setLevelLabel] = useState(existing?.level_label ?? '');
  const [audience, setAudience] = useState(existing?.audience ?? '');
  const [isActive, setIsActive] = useState(existing?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const total = Number(totalPrice);
    if (!name.trim() || isNaN(total) || total < 0) {
      setBusy(false); setErr('Name and a non-negative total price are required.'); return;
    }
    const payload = {
      club_id: clubId,
      name: name.trim(),
      package_type: packageType,
      description: description.trim() || null,
      schedule_text: scheduleText.trim() || null,
      duration_minutes: duration ? Number(duration) : null,
      num_sessions: Number(numSessions) || 1,
      total_price: total,
      per_session_price: perSessionPrice ? Number(perSessionPrice) : null,
      currency: 'AED',
      level_label: levelLabel.trim() || null,
      audience: audience.trim() || null,
      is_active: isActive,
      display_order: existing?.display_order ?? nextDisplayOrder,
    };
    const q = isNew
      ? (supabase.from as any)('club_packages').insert(payload)
      : (supabase.from as any)('club_packages').update(payload).eq('id', existing!.id);
    const { error } = await q;
    setBusy(false);
    if (error) setErr(error.message);
    else onSaved();
  };

  return (
    <Modal
      title={isNew ? 'New package' : 'Edit package'}
      subtitle="Customers see this on your brand page."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Adult beginner group, 8 weeks" autoFocus />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <Select value={packageType} onChange={(e) => setPackageType(e.target.value as PackageType)}>
              <option value="group_class">Group class</option>
              <option value="private_lesson">Private bundle</option>
              <option value="clinic">Clinic</option>
              <option value="camp">Camp</option>
              <option value="membership">Membership</option>
              <option value="other">Other</option>
            </Select>
          </Field>
          <Field label="Visibility">
            <Select value={isActive ? '1' : '0'} onChange={(e) => setIsActive(e.target.value === '1')}>
              <option value="1">Live — show on customer page</option>
              <option value="0">Hidden — draft</option>
            </Select>
          </Field>
        </div>

        <Field label="Description (optional)">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
            rows={3} placeholder="Who it's for, what's included, any prerequisites." />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Audience (optional)">
            <Input value={audience} onChange={(e) => setAudience(e.target.value)}
              placeholder="Adults · Kids 6–12 · Pro players" />
          </Field>
          <Field label="Level (optional)">
            <Input value={levelLabel} onChange={(e) => setLevelLabel(e.target.value)}
              placeholder="Beginner · Intermediate · Advanced" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Sessions">
            <Input type="number" min={1} value={numSessions} onChange={(e) => setNumSessions(e.target.value)} />
          </Field>
          <Field label="Duration (min)">
            <Input type="number" min={0} value={duration} onChange={(e) => setDuration(e.target.value)}
              placeholder="60" />
          </Field>
          <Field label="Schedule">
            <Input value={scheduleText} onChange={(e) => setScheduleText(e.target.value)}
              placeholder="Tue & Thu 6pm" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Total price (AED)">
            <Input type="number" min={0} step="any" value={totalPrice}
              onChange={(e) => setTotalPrice(e.target.value)} placeholder="800" />
          </Field>
          <Field label="Per-session price (AED, optional)">
            <Input type="number" min={0} step="any" value={perSessionPrice}
              onChange={(e) => setPerSessionPrice(e.target.value)} placeholder="100" />
          </Field>
        </div>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">
            {isNew ? 'Create package' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
